/**
 * 💰 useAffiliateCommissions Hook
 * Provides commission data with filtering
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  MOCK_COMMISSIONS,
  MOCK_COMMISSION_SUMMARY,
  getCommissionStatusConfig,
  getCommissionTypeLabel,
  type Commission,
  type CommissionType,
  type CommissionStatus,
} from '@/lib/affiliate-mock-data';

// ============================================
// TYPES
// ============================================

interface CommissionSummary {
  totalEarned: number;
  pendingAmount: number;
  releasedAmount: number;
  paidOutAmount: number;
}

interface CommissionFilters {
  type: CommissionType | 'all';
  status: CommissionStatus | 'all';
}

interface UseCommissionsReturn {
  commissions: Commission[];
  summary: CommissionSummary;
  isLoading: boolean;
  error: Error | null;
  filters: CommissionFilters;
  setTypeFilter: (type: CommissionType | 'all') => void;
  setStatusFilter: (status: CommissionStatus | 'all') => void;
  resetFilters: () => void;
  refetch: () => void;
  getStatusBadge: typeof getCommissionStatusConfig;
  getTypeLabel: typeof getCommissionTypeLabel;
}

// ============================================
// HOOK IMPLEMENTATION
// ============================================

export function useAffiliateCommissions(): UseCommissionsReturn {
  const [allCommissions, setAllCommissions] = useState<Commission[]>([]);
  const [summary, setSummary] = useState<CommissionSummary>(MOCK_COMMISSION_SUMMARY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  
  const [filters, setFilters] = useState<CommissionFilters>({
    type: 'all',
    status: 'all',
  });

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 200));
      setAllCommissions(MOCK_COMMISSIONS);
      setSummary(MOCK_COMMISSION_SUMMARY);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch commissions'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredCommissions = useMemo(() => {
    return allCommissions.filter(c => {
      const matchesType = filters.type === 'all' || c.type === filters.type;
      const matchesStatus = filters.status === 'all' || c.status === filters.status;
      return matchesType && matchesStatus;
    });
  }, [allCommissions, filters]);

  return {
    commissions: filteredCommissions,
    summary,
    isLoading,
    error,
    filters,
    setTypeFilter: (type) => setFilters(f => ({ ...f, type })),
    setStatusFilter: (status) => setFilters(f => ({ ...f, status })),
    resetFilters: () => setFilters({ type: 'all', status: 'all' }),
    refetch: fetchData,
    getStatusBadge: getCommissionStatusConfig,
    getTypeLabel: getCommissionTypeLabel,
  };
}

export default useAffiliateCommissions;
